import { useState, type ReactNode } from 'react';
import { mapAuthError } from '@mybusinessmate/api-client';
import { authClient } from '../../lib/api';
import { useReauthStore, viewBackupCodes } from '../../lib/reauth';
import { useToast } from '../ui/toast';
import type { useReauth } from './reauth';
import { BackupCodesDialog, TurnOffTwoFactorDialog, TwoFactorSetupDialog } from './two-factor-dialogs';

type Guard = ReturnType<typeof useReauth>['guard'];

export interface TwoFactorControlsOptions {
  /** The re-ask gate from `useReauth` / `useStaffReauth` (the same one the page uses for its other sensitive actions). */
  guard: Guard;
  /** The workspace requires two-step from this person, so it cannot be turned off, only moved to a new phone. */
  mandatory?: boolean;
  audience: 'client' | 'staff';
  /** Two-step was turned on or off: refresh whatever shows it. */
  onChanged: () => void;
  /** Where a failure goes (a banner on the page). */
  onProblem: (message: string) => void;
}

/**
 * Everything two-step does on a Security page, for the client app and the staff app alike: turning it on (QR code,
 * first code, backup codes), turning it off, viewing the backup codes and getting new ones. Each action goes through
 * the password re-ask. The pages only decide how the rows look (their own Figma frames) and call these actions.
 */
export function useTwoFactorControls({
  guard,
  mandatory = false,
  audience,
  onChanged,
  onProblem,
}: TwoFactorControlsOptions): {
  /** Turn on, or move to a new phone. */
  startSetup: () => void;
  /** Turn off (or explain that it is required). */
  requestTurnOff: () => void;
  viewCodes: () => void;
  /** Render once in the page. */
  dialogs: ReactNode;
} {
  const toast = useToast();
  const [setup, setSetup] = useState<{ open: boolean; password?: string }>({ open: false });
  const [turnOff, setTurnOff] = useState<{ open: boolean; password?: string }>({ open: false });
  const [codes, setCodes] = useState<string[]>([]);

  // Better Auth needs the password to start enrolling, so this always asks, even inside the unlocked window.
  const startSetup = () =>
    guard('turn on two-step sign-in', (password) => setSetup({ open: true, password }), { always: true });

  const requestTurnOff = () =>
    mandatory
      ? setTurnOff({ open: true })
      : guard('turn off two-step verification', (password) => setTurnOff({ open: true, password }), { always: true });

  const showCodes = (password?: string) =>
    void viewBackupCodes(password).then((result) => {
      if ('codes' in result) setCodes(result.codes);
      else if (result.reauth) {
        // The server's window closed before ours did (for example it restarted): ask again.
        useReauthStore.getState().clear();
        guard('see your backup codes', showCodes);
      } else onProblem(result.message);
    });
  const viewCodes = () => guard('see your backup codes', showCodes);

  const getNewCodes = () =>
    guard(
      'get new backup codes',
      async (password) => {
        if (!password) return;
        const { data, error } = await authClient.twoFactor.generateBackupCodes({ password });
        const mapped = mapAuthError(error);
        if (mapped || !data) {
          onProblem(mapped?.message ?? 'Something went wrong.');
          return;
        }
        setCodes(data.backupCodes);
        toast({ title: 'New backup codes are ready', description: 'The old ones no longer work.' });
      },
      { always: true },
    );

  const dialogs = (
    <>
      <TwoFactorSetupDialog
        open={setup.open}
        password={setup.password}
        onClose={() => setSetup({ open: false })}
        onEnabled={onChanged}
      />
      <TurnOffTwoFactorDialog
        open={turnOff.open}
        password={turnOff.password}
        requiredByPolicy={mandatory}
        audience={audience}
        onClose={() => setTurnOff({ open: false })}
        onMoveToNewPhone={() => {
          setTurnOff({ open: false });
          startSetup();
        }}
        onDisabled={onChanged}
      />
      <BackupCodesDialog
        open={codes.length > 0}
        onClose={() => setCodes([])}
        codes={codes}
        variant="view"
        onGetNew={() => {
          setCodes([]);
          getNewCodes();
        }}
      />
    </>
  );

  return { startSetup, requestTurnOff, viewCodes, dialogs };
}
